import { useCallback, useEffect, useReducer, useRef, useState } from 'react'
import {
    BACKGROUND_EFFECT_VIDEO_PRESET,
    VIDEO_PRESETS,
    getGetUserMediaVideoConstraints,
    getDefaultAudioConstraints,
    meetsEffectCapture,
    videoPresetForBackgroundEffect,
} from '../config/mediaConfig'
import {
    LIVEKIT_BACKGROUND_EFFECT_CHANGED_EVENT,
    LIVEKIT_SET_BACKGROUND_EFFECT_EVENT,
    NO_BACKGROUND_EFFECT,
    PROCESSOR_START_TIMEOUT_MS,
    backgroundEffectsEqual,
    backgroundProblemHoldsCamera,
    isBackgroundEffectActive,
    isBackgroundEffectAvailable,
    requestBackgroundEffect,
    type BackgroundEffect,
    type BackgroundProblem,
} from '../services/liveKitVideoBackground'
import {
    readCachedBackgroundEffect,
    readCameraWithoutBackgroundOnThisDevice,
    saveBackgroundEffectPreference,
    writeCameraWithoutBackgroundOnThisDevice,
} from '../services/backgroundEffectPreference'
import {
    attachPreviewBackgroundEffect,
    type PreviewBackgroundHandle,
} from '../services/previewVideoBackground'
import { toMediaTrackConstraints } from '../services/livekitNetworkResilience'
import { PREVIEW_PAINT_TIMEOUT_MS, whenVideoPainted } from '../utils/whenVideoPainted'
import {
    readMediaPreferenceForJoin,
    writeMediaPreference,
    type MediaPreferenceKind,
} from '../utils/mediaPreferences'

/**
 * Types for media device state and actions
 */
export interface MediaDeviceState {
    videoDevices: MediaDeviceInfo[]
    audioDevices: MediaDeviceInfo[]
    selectedVideoDevice: string
    selectedAudioDevice: string
    stream: MediaStream | null
    videoEnabled: boolean
    audioEnabled: boolean
    loading: boolean
    initializing: boolean
    error: string | null
    cameraError: string | null
    microphoneError: string | null
    permissionsGranted: boolean
    permissionsRequested: boolean
    /**
     * The device is off because an error turned it off (a denied prompt, a busy
     * or missing device), not because the user chose it. Join saves it with a
     * marker so the next Join screen tries the device again.
     */
    videoForcedOff: boolean
    audioForcedOff: boolean
}

type MediaDeviceAction =
    | { type: 'INIT' }
    | { type: 'SET_DEVICES'; videoDevices: MediaDeviceInfo[]; audioDevices: MediaDeviceInfo[] }
    | { type: 'SET_SELECTED_VIDEO_DEVICE'; deviceId: string }
    | { type: 'SET_SELECTED_AUDIO_DEVICE'; deviceId: string }
    | { type: 'SET_STREAM'; stream: MediaStream | null }
    | { type: 'SET_VIDEO_ENABLED'; enabled: boolean }
    | { type: 'SET_AUDIO_ENABLED'; enabled: boolean }
    | { type: 'SET_LOADING'; loading: boolean }
    | { type: 'SET_INITIALIZING'; initializing: boolean }
    | { type: 'SET_ERROR'; error: string | null }
    | { type: 'SET_CAMERA_ERROR'; cameraError: string | null }
    | { type: 'SET_MICROPHONE_ERROR'; microphoneError: string | null }
    | { type: 'SET_PERMISSIONS_GRANTED'; granted: boolean }
    | { type: 'SET_PERMISSIONS_REQUESTED'; requested: boolean }
    | { type: 'SET_FORCED_OFF'; kind: MediaPreferenceKind; forced: boolean }
    | { type: 'RESET' }

const initialMediaDeviceState: MediaDeviceState = {
    videoDevices: [],
    audioDevices: [],
    selectedVideoDevice: '',
    selectedAudioDevice: '',
    stream: null,
    videoEnabled: true,
    audioEnabled: true,
    loading: false,
    initializing: true,
    error: null,
    cameraError: null,
    microphoneError: null,
    permissionsGranted: false,
    permissionsRequested: false,
    videoForcedOff: false,
    audioForcedOff: false,
}

function mediaDeviceReducer(state: MediaDeviceState, action: MediaDeviceAction): MediaDeviceState {
    switch (action.type) {
        case 'INIT':
            return { ...initialMediaDeviceState, initializing: true }
        case 'SET_DEVICES':
            return { ...state, videoDevices: action.videoDevices, audioDevices: action.audioDevices }
        case 'SET_SELECTED_VIDEO_DEVICE':
            return { ...state, selectedVideoDevice: action.deviceId }
        case 'SET_SELECTED_AUDIO_DEVICE':
            return { ...state, selectedAudioDevice: action.deviceId }
        case 'SET_STREAM':
            return { ...state, stream: action.stream }
        case 'SET_VIDEO_ENABLED':
            return { ...state, videoEnabled: action.enabled }
        case 'SET_AUDIO_ENABLED':
            return { ...state, audioEnabled: action.enabled }
        case 'SET_LOADING':
            return { ...state, loading: action.loading }
        case 'SET_INITIALIZING':
            return { ...state, initializing: action.initializing }
        case 'SET_ERROR':
            return { ...state, error: action.error }
        case 'SET_CAMERA_ERROR':
            return { ...state, cameraError: action.cameraError }
        case 'SET_MICROPHONE_ERROR':
            return { ...state, microphoneError: action.microphoneError }
        case 'SET_PERMISSIONS_GRANTED':
            return { ...state, permissionsGranted: action.granted }
        case 'SET_PERMISSIONS_REQUESTED':
            return { ...state, permissionsRequested: action.requested }
        case 'SET_FORCED_OFF':
            return action.kind === 'video'
                ? { ...state, videoForcedOff: action.forced }
                : { ...state, audioForcedOff: action.forced }
        case 'RESET':
            return { ...initialMediaDeviceState }
        default:
            return state
    }
}

/**
 * Errors that belong to one device (busy, missing, unable to meet the
 * constraints), so the other device may still open on its own. A permission
 * refusal is not one of them: retrying it would show Firefox and Safari users
 * a second prompt for the access they just declined.
 */
const DEVICE_LEVEL_ERROR_NAMES = new Set([
    'NotReadableError',
    'AbortError',
    'NotFoundError',
    'OverconstrainedError',
])

const mediaErrorName = (error: unknown): string =>
    error && typeof error === 'object' && 'name' in error ? String((error as Error).name) : ''

const isDeviceLevelError = (error: unknown) => DEVICE_LEVEL_ERROR_NAMES.has(mediaErrorName(error))

/** Another app holds the device, or the OS refused to start it. */
const isBusyDeviceError = (error: unknown) => {
    const name = mediaErrorName(error)
    return name === 'AbortError' || name === 'NotReadableError'
}

const CAMERA_BUSY_MESSAGE =
    'Camera could not be started. It may be in use by another application or not connected. Please close other apps or reconnect your camera and try again.'

const MICROPHONE_BUSY_MESSAGE =
    'Microphone could not be started. It may be in use by another application or not connected. Please close other apps or reconnect your microphone and try again.'

/** What to tell the user about the one device that failed to open. */
function describeDeviceFailure(device: 'camera' | 'microphone', error: unknown): string {
    if (isBusyDeviceError(error)) {
        return device === 'camera' ? CAMERA_BUSY_MESSAGE : MICROPHONE_BUSY_MESSAGE
    }
    const name = mediaErrorName(error)
    const label = device === 'camera' ? 'Camera' : 'Microphone'
    if (name === 'NotFoundError') {
        return `${label} not found. Please check that it is connected and try again.`
    }
    if (name === 'OverconstrainedError') {
        return `The selected ${device} does not support the required settings. Please try a different ${device}.`
    }
    return `${label} could not be started. Please check it and try again.`
}

interface InitialStreamResult {
    stream: MediaStream
    /** Set when the camera was requested but would not open; the mic opened alone. */
    videoError: unknown
    /** Set when the microphone was requested but would not open; the camera opened alone. */
    audioError: unknown
}

/**
 * The first getUserMedia of the Join screen.
 *
 * A combined request is all or nothing: one busy or missing device rejects it,
 * and the working one is lost with it. So when both were asked for and the
 * failure is device-level, each is tried alone and only the one that fails is
 * reported. Throws when nothing could be opened.
 */
async function acquireInitialStream(
    video: MediaTrackConstraints | boolean,
    audio: MediaTrackConstraints | boolean
): Promise<InitialStreamResult> {
    try {
        const stream = await navigator.mediaDevices.getUserMedia({ video, audio })
        return { stream, videoError: null, audioError: null }
    } catch (combinedError) {
        if (!video || !audio || !isDeviceLevelError(combinedError)) throw combinedError

        let audioError: unknown
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio })
            return { stream, videoError: combinedError, audioError: null }
        } catch (error) {
            // A refusal here is the user's answer: asking again for the camera
            // would be a second prompt.
            if (!isDeviceLevelError(error)) throw error
            audioError = error
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video })
            return { stream, videoError: null, audioError }
        } catch {
            throw combinedError
        }
    }
}

/**
 * True only when the browser says camera and microphone are both granted
 * already. Anything else - no Permissions API, a browser that cannot answer for
 * these devices (it throws), or a device not granted yet - is false.
 */
async function bothDevicesAlreadyGranted(): Promise<boolean> {
    try {
        const permissions = navigator.permissions
        if (!permissions?.query) return false
        const [camera, microphone] = await Promise.all([
            permissions.query({ name: 'camera' as PermissionName }),
            permissions.query({ name: 'microphone' as PermissionName }),
        ])
        return camera.state === 'granted' && microphone.state === 'granted'
    } catch {
        return false
    }
}

/**
 * A list that cannot name its devices: before a grant for that kind, browsers
 * list them with blank ids.
 */
export function listedWithoutIds(devices: MediaDeviceInfo[]): boolean {
    return devices.length > 0 && devices.every((device) => !device.deviceId)
}

/**
 * The saved device when it is listed, or when the list cannot say: a blank list
 * hides the saved device, it does not prove it stale. Otherwise the first
 * listed, or none when nothing of that kind is listed (a select value with no
 * option). A blank list does not last: `getMediaStream` lists again once that
 * kind has opened, so the select never keeps an id it has no option for.
 */
export function pickListedDevice(saved: string, devices: MediaDeviceInfo[]): string {
    if (saved && devices.some((device) => device.deviceId === saved)) return saved
    if (saved && listedWithoutIds(devices)) return saved
    return devices[0]?.deviceId || ''
}

/**
 * Custom React hook for managing media devices (camera/microphone) and permissions.
 * Handles device enumeration, selection, toggling, and error handling.
 *
 * @param open Whether the dialog/component using this hook is open (triggers initialization)
 */
export function useMediaDevices(open: boolean) {
    const [state, dispatch] = useReducer(mediaDeviceReducer, initialMediaDeviceState)
    const videoRef = useRef<HTMLVideoElement>(null)
    const currentStreamRef = useRef<MediaStream | null>(null)
    const previewStopRef = useRef<(() => void) | null>(null)
    /**
     * The lists the selects show, for `getMediaStream` to tell a blank one from a
     * real one. A ref because init calls the first render's `getMediaStream`,
     * whose state is the empty initial one.
     */
    const listedDevicesRef = useRef<{ video: MediaDeviceInfo[]; audio: MediaDeviceInfo[] }>({
        video: [],
        audio: [],
    })

    /**
     * SC-158: the background effect chosen here, before entering the office.
     *
     * Seeded from the local cache so the dialog opens on the remembered choice
     * rather than flashing "no effect". Kept outside the reducer deliberately —
     * this file carries the SC-273 camera-lock handling, and widening the media
     * state machine for a preview-only concern is not worth the churn.
     */
    const [backgroundEffect, setBackgroundEffect] = useState<BackgroundEffect>(
        () => readCachedBackgroundEffect()
    )
    /**
     * Latest-ref for the chosen effect, read by `getMediaStream` when it picks a
     * capture resolution. A ref rather than a dependency: adding one to
     * `getMediaStream` would rebuild it and retrigger every effect keyed on it,
     * which is the camera-acquisition path SC-273 hardened.
     */
    const backgroundEffectRef = useRef<BackgroundEffect>(NO_BACKGROUND_EFFECT)
    const previewEffectAbortRef = useRef<AbortController | null>(null)
    /**
     * The capture raise still in flight, and the track it is on. A preview
     * processor for that track starts only once it has settled; see the raise
     * effect below for why.
     */
    const captureRaiseRef = useRef<{ track: MediaStreamTrack; settled: Promise<void> } | null>(
        null
    )

    useEffect(() => {
        backgroundEffectRef.current = backgroundEffect
    }, [backgroundEffect])

    /**
     * SC-158: keep the chosen effect current while the dialog sits closed.
     *
     * The in-call "Fix" dialog stays mounted for the whole call, so a value
     * seeded once at mount goes stale as soon as the effect is changed from the
     * toolbar. The dialog would then label and preview an effect the call is not
     * publishing.
     *
     * Two sources, because neither covers every case alone:
     * - the window events. Every in-call change is announced, as a request (set)
     *   or an outcome (changed), including the connector going back to the
     *   running effect when a new pick failed;
     * - the cache, re-read on open, for a change that arrived any other way.
     *
     * An event seen while closed beats the cache on open. A pick that failed is
     * never saved, but an event is the more direct account of what the call is
     * showing.
     */
    const effectEventSinceCloseRef = useRef(false)

    useEffect(() => {
        const adopt = (event: Event) => {
            const next = (event as CustomEvent<{ effect?: BackgroundEffect }>).detail?.effect
            if (!next) return
            effectEventSinceCloseRef.current = true
            // The call changed the choice under a pick still loading (a sign-in
            // or out re-seeds it): that pick is abandoned, and Join must not
            // wait for it. Our own pick announces itself here too, equal to the
            // pending one, and is kept.
            const pending = pendingSaveRef.current
            if (pending && !backgroundEffectsEqual(pending, next)) setPendingSave(null)
            setBackgroundEffect((current) => (backgroundEffectsEqual(current, next) ? current : next))
            // Our own pick announces itself here too; anything else replaces
            // the choice without being picked over the raw view.
            setRawHold((hold) => (hold && backgroundEffectsEqual(hold.effect, next) ? hold : null))
        }
        window.addEventListener(LIVEKIT_SET_BACKGROUND_EFFECT_EVENT, adopt)
        window.addEventListener(LIVEKIT_BACKGROUND_EFFECT_CHANGED_EVENT, adopt)
        return () => {
            window.removeEventListener(LIVEKIT_SET_BACKGROUND_EFFECT_EVENT, adopt)
            window.removeEventListener(LIVEKIT_BACKGROUND_EFFECT_CHANGED_EVENT, adopt)
        }
    }, [])

    useEffect(() => {
        if (!open) {
            // A pick that had not landed was abandoned with the dialog: the
            // reopened dialog shows the saved effect, so a later Join must not
            // save the abandoned one. The connector was told about that pick
            // when it was made and keeps it, so it is put back on the saved
            // choice here: otherwise a call started from the Join screen's
            // parked pick would run an effect that was never saved.
            if (pendingSaveRef.current) requestBackgroundEffect(readCachedBackgroundEffect(), false)
            setPendingSave(null)
            effectEventSinceCloseRef.current = false
            return
        }
        if (effectEventSinceCloseRef.current) return
        const cached = readCachedBackgroundEffect()
        setBackgroundEffect((current) => (backgroundEffectsEqual(current, cached) ? current : cached))
    }, [open])

    /**
     * Raise the capture resolution of the **existing** track when an effect is
     * switched on mid-session. [SC-158]
     *
     * Without this the resolution would only be right for someone who already had
     * an effect when the camera started: turning the first one on would leave a
     * 360p track, and the effect would look worse than it does on the next visit
     * — the same inconsistency in reverse.
     *
     * `applyConstraints` rather than re-acquiring: it keeps the same track.
     *
     * **The preview processor should not start until this has settled.** The
     * patched library follows a frame-size change, but at a cost: it clears the
     * mask to background, so the person is gone until the next segmentation
     * lands, and it reallocates its buffers and crops an image background again.
     * A processor started at 360p and then fed 720p frames pays all of that
     * right as the preview appears. The attach below waits on
     * {@link captureRaiseRef} only just before `init()` for that reason, so the
     * image download still runs alongside the raise.
     *
     * Best-effort. A camera that will not honour the constraint keeps the
     * resolution it has, which is exactly today's behaviour — and then the size
     * `init()` reads matches the frames anyway.
     *
     * Only for an effect that will actually run: a preference carried to a
     * device or deployment that cannot run one must not cost it a 720p camera.
     */
    useEffect(() => {
        const track = state.stream?.getVideoTracks()[0]
        if (!track || track.readyState !== 'live') return
        if (!isBackgroundEffectActive(backgroundEffect)) return

        // Feature-detected rather than assumed. This runs in a passive effect, so a
        // track that does not implement the constraint API — an unusual source, a
        // test double — must leave the resolution alone, never throw and take the
        // preview down with it.
        if (typeof track.getSettings !== 'function' || typeof track.applyConstraints !== 'function') {
            return
        }

        const target = VIDEO_PRESETS[BACKGROUND_EFFECT_VIDEO_PRESET]
        // A 720p camera still at 30 fps needs the rate cap too, which is where the
        // effect's CPU/GPU saving is (SC-478).
        if (meetsEffectCapture(track.getSettings())) return

        // Bounded like a processor start: a camera that never answers must not
        // keep the preview covered for good.
        let timer: ReturnType<typeof setTimeout> | undefined
        const settled = Promise.race([
            track
                .applyConstraints(toMediaTrackConstraints(target))
                .catch((error) => {
                    console.warn(
                        '[useMediaDevices] SC-158: could not raise capture resolution for the effect:',
                        error
                    )
                }),
            new Promise<void>((resolve) => {
                timer = setTimeout(resolve, PROCESSOR_START_TIMEOUT_MS)
            }),
        ]).finally(() => {
            clearTimeout(timer)
            if (captureRaiseRef.current?.settled === settled) captureRaiseRef.current = null
        })
        captureRaiseRef.current = { track, settled }
    }, [backgroundEffect, state.stream])
    /**
     * The processed stream, when an effect is active. Held as state rather than
     * written straight onto the video element so that every surface showing the
     * preview — the card and the effects dialog — binds the same object through
     * React, instead of racing each other over `srcObject`.
     */
    const [processedPreviewStream, setProcessedPreviewStream] = useState<MediaStream | null>(null)
    /**
     * The live processor, kept across effect changes. Bound to one specific
     * track, so it is torn down and rebuilt only when the stream itself changes.
     */
    const previewHandleRef = useRef<
        { track: MediaStreamTrack; handle: PreviewBackgroundHandle } | null
    >(null)
    /**
     * What the processed stream is rendering right now — the effect last
     * switched to successfully, or "none" for the processor's pass-through.
     * Differs from `backgroundEffect` while a new pick loads, and is what a
     * failed switch falls back to.
     */
    const [previewShowing, setPreviewShowing] = useState<BackgroundEffect>(NO_BACKGROUND_EFFECT)
    const previewShowingRef = useRef<BackgroundEffect>(NO_BACKGROUND_EFFECT)
    const showInPreview = useCallback((effect: BackgroundEffect) => {
        previewShowingRef.current = effect
        setPreviewShowing(effect)
    }, [])
    /**
     * An in-place switch under way. The previous effect stays on screen until
     * the new one is in, and is never covered: the patched library swaps
     * atomically (patches/@livekit+track-processors), so there is no empty
     * background to hide. Only the progress bar shows it.
     */
    const [previewSwitching, setPreviewSwitching] = useState<'download' | null>(null)
    /** Why the selected effect is not on the preview as chosen, for the Join screen. */
    const [previewProblem, setPreviewProblem] = useState<BackgroundProblem | null>(null)
    /** Bumped by Retry, to run the preview effect again for the same choice. */
    const [previewRetryToken, setPreviewRetryToken] = useState(0)
    const previewRetryTokenRef = useRef(0)
    /** "Turn on camera without background", for a device that cannot run effects. */
    const [cameraWithoutBackground, setCameraWithoutBackground] = useState(() =>
        readCameraWithoutBackgroundOnThisDevice()
    )
    /**
     * A pick saved only once the preview shows it: one that fails while another
     * effect is on screen is not kept, and the one on screen stays the choice.
     */
    const pendingSaveRef = useRef<BackgroundEffect | null>(null)
    /**
     * Whether a pick is waiting in {@link pendingSaveRef}, for render: the ref
     * itself cannot re-render the Join button that must wait for it.
     */
    const [hasPendingSave, setHasPendingSave] = useState(false)
    const setPendingSave = useCallback((effect: BackgroundEffect | null) => {
        pendingSaveRef.current = effect
        setHasPendingSave(effect !== null)
    }, [])
    /**
     * The in-place switch still under way, if any. While one is, what the
     * preview shows is about to change, so a pick of the effect on screen is
     * not "already on screen": it needs its own switch to win over that one.
     */
    const previewSwitchPendingRef = useRef<object | null>(null)
    /**
     * What the processor itself renders, which `previewShowingRef` does not
     * always track: a switch the user has since left can still land (one the
     * processor had begun cannot stand down), and it is deliberately not shown
     * as the selection. A restore to the effect on the label must still put
     * the processor back on it (R8-N1).
     */
    const previewProcessorShowsRef = useRef<BackgroundEffect>(NO_BACKGROUND_EFFECT)
    /**
     * Every in-place switch not yet settled, the abandoned ones included. Until
     * they are, any of them may still land on the processor, so what it shows
     * is not settled either.
     */
    const previewSwitchesInFlightRef = useRef(new Set<object>())
    /**
     * A pick made while the raw camera was what the preview showed, uncovered:
     * "No effect" was the selection and on screen. Until that pick is on
     * screen, the raw view it was made over stays (see `previewRawHeld`).
     *
     * Bound to the camera track it was made over, so a device switch or a
     * camera toggle ends it, and set only by a pick in this dialog
     * (`handleBackgroundEffectChange`): an effect that arrives any other way
     * was not chosen over this view.
     */
    const [rawHold, setRawHold] = useState<
        { effect: BackgroundEffect; track: MediaStreamTrack } | null
    >(null)
    /** The camera track the preview shows raw and uncovered right now, if it does. */
    const rawViewOnScreenRef = useRef<MediaStreamTrack | null>(null)
    /**
     * A processor built for a pick held over the raw view: `from` is that raw
     * view, `to` the processed stream replacing it. Rebinding the element
     * blanks it until `to` decodes, so the surfaces keep `from` beneath it for
     * that moment. Ends once this hook's element has painted `to`: from then on
     * the effect is the current view, and a surface opened later must never be
     * offered the raw camera to put beneath it (R13-B1). A surface still mid-
     * handover at that point finishes its own (see BackgroundEffectsDialog).
     */
    const [previewHandover, setPreviewHandover] = useState<
        { from: MediaStream; to: MediaStream } | null
    >(null)
    /** The raw stream a hold is keeping on screen, for the attach that ends it. */
    const rawHeldStreamRef = useRef<MediaStream | null>(null)
    /**
     * The stream the video element has actually **painted a frame of**.
     *
     * Stored as the stream itself, never as a boolean. A boolean lags by exactly
     * one render: when the processed stream arrives, React re-renders with the
     * pending flag already false while the effect that would reset the boolean
     * has not run yet — so for that one commit the cover lifts over an element
     * that is still blank. Holding the identity lets the answer be *derived*
     * during render, which is the same reason `previewEffectPending` below is
     * derived rather than stored.
     */
    const [paintedStream, setPaintedStream] = useState<MediaStream | null>(null)

    // Clean up stream on unmount or when dialog closes
    useEffect(() => {
        return () => {
            previewEffectAbortRef.current?.abort()
            previewEffectAbortRef.current = null
            if (previewStopRef.current) {
                previewStopRef.current()
                previewStopRef.current = null
            }
            if (currentStreamRef.current) {
                currentStreamRef.current.getTracks().forEach((track) => track.stop())
                currentStreamRef.current = null
            }
        }
    }, [])

    // Initialize devices when dialog opens
    useEffect(() => {
        if (!open) return
        dispatch({ type: 'INIT' })
        dispatch({ type: 'SET_PERMISSIONS_REQUESTED', requested: false })
        dispatch({ type: 'SET_ERROR', error: null })
        dispatch({ type: 'SET_CAMERA_ERROR', cameraError: null })
        dispatch({ type: 'SET_MICROPHONE_ERROR', microphoneError: null })

        // Clean up any existing stream
        if (currentStreamRef.current) {
            currentStreamRef.current.getTracks().forEach((track) => track.stop())
            currentStreamRef.current = null
            dispatch({ type: 'SET_STREAM', stream: null })
        }

        let cancelled = false
        const timer = setTimeout(() => {
            requestPermissionsAndEnumerateDevices().catch((err) => {
                if (!cancelled) {
                    dispatch({ type: 'SET_ERROR', error: 'Failed to access devices.' })
                }
            })
        }, 300)

        return () => {
            cancelled = true
            clearTimeout(timer)
            if (currentStreamRef.current) {
                currentStreamRef.current.getTracks().forEach((track) => track.stop())
                currentStreamRef.current = null
                dispatch({ type: 'SET_STREAM', stream: null })
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    /**
     * Whether the raw camera may be shown at all: only with no effect selected,
     * or where effects cannot run and the user chose the camera without one on
     * this device.
     */
    const rawPreviewAllowed =
        backgroundEffect.kind === 'none' ||
        (!isBackgroundEffectAvailable() && cameraWithoutBackground)

    const hasPreviewVideo = state.videoEnabled && (state.stream?.getVideoTracks().length ?? 0) > 0
    const previewProblemHolds = backgroundProblemHoldsCamera(previewProblem)
    const previewTrack = state.stream?.getVideoTracks()[0] ?? null

    /**
     * The preview rule, as the user set it (SC-158): the preview never *falls
     * back* to the raw camera. But when the raw camera is the current view —
     * "No effect" selected and on screen — a new pick keeps that view, uncovered,
     * until the effect has a composited frame, and then switches. Nothing else
     * is shown in between: no cover, no blank, no frozen frame.
     *
     * Only for the pick it was made for, on the track it was made over, and
     * only until something else is known: the effect on screen ends it, and so
     * does a problem — a pick that fails is covered and explained, never left
     * on the raw view. From an effect, the effect on screen is the current
     * view, and that swap is the library's atomic one.
     */
    const previewRawHeld =
        rawHold !== null &&
        hasPreviewVideo &&
        rawHold.track === previewTrack &&
        backgroundEffectsEqual(rawHold.effect, backgroundEffect) &&
        previewShowing.kind === 'none' &&
        !previewProblemHolds

    // Set up video stream when stream or videoEnabled changes.
    // The video element is now always mounted (hidden via CSS), so we must
    // ensure srcObject stays attached even after a toggle cycle.
    //
    // With an effect selected the raw stream is never bound, not merely
    // covered: until the processed stream exists the element shows nothing —
    // unless it is the view the pick was made over (`previewRawHeld`).
    const previewStream =
        processedPreviewStream ?? (rawPreviewAllowed || previewRawHeld ? state.stream : null)
    rawHeldStreamRef.current = previewRawHeld ? state.stream : null

    /**
     * The raw view a surface may keep **beneath** its element while a pick
     * held over it takes the element over: during the hold, so the surface can
     * have it painted before the processed stream arrives, and through the
     * handover to it, which ends once this hook's element has painted the
     * processed stream. Never a view of its own: a surface uses it only while its
     * element last painted exactly this stream, and only until it paints the
     * processed one.
     */
    const previewHandoverFrom = previewRawHeld
        ? state.stream
        : previewHandover !== null &&
            !previewProblemHolds &&
            previewHandover.to === processedPreviewStream &&
            previewHandover.from === state.stream
          ? previewHandover.from
          : null

    /**
     * True while a processor is being built for an effect the user has already
     * chosen.
     *
     * **Derived, not stored.** Setting it from inside the effect made it true
     * one render too late: the render that first put the raw stream on the
     * element had already been painted, so the sharp unprocessed camera showed
     * for a frame every time the dialog opened. Computing it here makes it true
     * in the very same render the stream arrives.
     *
     * A processor is bound to one track, so first camera start and every camera
     * off→on genuinely have to build one, and that means loading WASM, a
     * segmentation model and possibly an image. Surfaces cover that window
     * rather than showing a frame they are about to replace.
     */
    /**
     * Whether the element has painted a frame of the stream it is *currently*
     * bound to.
     *
     * "The stream exists" and "the element is showing it" are different moments:
     * assigning `srcObject` blanks the element until the new stream decodes.
     * Comparing identities means this flips to false in the very same render the
     * stream is swapped, so nothing can uncover a blank element.
     */
    const previewPainted = previewStream !== null && paintedStream === previewStream

    /**
     * {@link previewHandoverFrom} for this hook's own element: set only while
     * the element last painted that raw view, so a surface opened later never
     * gets one.
     */
    const previewUnderlayStream =
        previewHandoverFrom !== null && paintedStream === previewHandoverFrom
            ? previewHandoverFrom
            : null

    /**
     * The preview is covered for the effect: never a fallback to the raw
     * camera, never an unchosen background. Up from the render an effect is
     * chosen until it is on screen — for the first attach, for a switch from
     * "no effect" (the processor is passing the raw camera through), and for as
     * long as the effect cannot be shown.
     *
     * Not while a pick is held over the raw view the user was already looking
     * at ({@link previewRawHeld}): that view stays until the effect is in. Not
     * for a switch between effects either: the previous effect stays on screen
     * until the new one is in, and the swap itself is atomic.
     */
    const previewEffectPending =
        hasPreviewVideo &&
        !rawPreviewAllowed &&
        (((!processedPreviewStream || previewShowing.kind === 'none') && !previewRawHeld) ||
            previewProblemHolds)

    // Read by the next pick, which is an event after this render committed.
    rawViewOnScreenRef.current =
        hasPreviewVideo &&
        previewShowing.kind === 'none' &&
        !previewEffectPending &&
        !previewProblemHolds &&
        previewPainted
            ? previewTrack
            : null

    /**
     * A pick that is not yet the saved choice is still loading: the Join screen
     * waits for it, because Join parks the connector's current pick and the call
     * would start with an effect that was never saved. Ends when the preview
     * shows it (the save lands), when it fails (a holding problem; a failed
     * switch clears the pick itself), or when nothing is loading any more: no
     * camera, or a device that shows the raw camera on purpose.
     */
    const backgroundEffectApplying =
        hasPendingSave && hasPreviewVideo && !rawPreviewAllowed && !previewProblemHolds

    /** Real work under way, for the progress bar: an attach or a switch. */
    const previewEffectWorking =
        hasPreviewVideo &&
        !rawPreviewAllowed &&
        !previewProblemHolds &&
        (!processedPreviewStream || previewSwitching !== null || previewShowing.kind === 'none')

    useEffect(() => {
        const element = videoRef.current
        if (!element) return

        if (!previewStream) {
            // Clear video element when stream is null
            element.srcObject = null
            return
        }

        // No reset needed: `previewPainted` compares identities, so it is already
        // false for this stream until the callback below records it.
        element.srcObject = previewStream
        // Ensure video plays when video is enabled
        if (state.videoEnabled) {
            element.play().catch((error) => {
                console.warn('[useMediaDevices] Video play failed:', error)
            })
        }

        let settled = false
        const markPainted = () => {
            if (settled) return
            settled = true
            setPaintedStream(previewStream)
            // The handover to this stream is over: in the same batch, so no
            // render offers the raw view after the effect has painted here.
            setPreviewHandover((handover) =>
                handover !== null && handover.to === previewStream ? null : handover
            )
        }

        // The first frame requestVideoFrameCallback reports as composited, which
        // is the only event that actually means "not black any more".
        // `loadeddata` is the fallback for browsers without it (Firefox): weaker,
        // because it fires on decode rather than paint, but far better than
        // uncovering on stream arrival.
        const cancelPaintWait = whenVideoPainted(
            element,
            { frames: 1, fallbackEvents: ['loadeddata'] },
            markPainted
        )

        // Bounded. A camera that never delivers a frame must not leave the
        // preview covered forever — showing it late beats never showing it.
        const timeout = setTimeout(markPainted, PREVIEW_PAINT_TIMEOUT_MS)

        return () => {
            settled = true
            clearTimeout(timeout)
            cancelPaintWait()
        }
    }, [previewStream, state.videoEnabled])

    /**
     * SC-158: mirror the chosen background effect onto the preview.
     *
     * Runs after the raw-attach effect above, so it replaces the element's
     * source rather than racing it. Re-runs whenever the stream is replaced (a
     * device switch, a camera toggle) because the processor is bound to one
     * specific track and cannot outlive it.
     *
     * The teardown is registered in `previewStopRef`, which the hook already
     * calls at each of the three moments that matter: unmount, acquiring a new
     * stream, and the handoff to LiveKit. That last one is the important one —
     * every processor holds a WebGL context, and the page has a hard ceiling on
     * those. The preview's context must be gone before the call builds its own.
     */
    const releasePreviewEffect = useCallback(() => {
        previewEffectAbortRef.current?.abort()
        previewEffectAbortRef.current = null
        previewSwitchPendingRef.current = null
        previewSwitchesInFlightRef.current.clear()
        previewProcessorShowsRef.current = NO_BACKGROUND_EFFECT
        const current = previewHandleRef.current
        previewHandleRef.current = null
        current?.handle.stop()
        setProcessedPreviewStream(null)
        setPreviewHandover(null)
        // Whatever the pick was held over, it is not on screen any more.
        setRawHold(null)
        showInPreview(NO_BACKGROUND_EFFECT)
        setPreviewSwitching(null)
    }, [showInPreview])

    /** The held pick is on screen now, or a newer pick holds instead. */
    const endRawHold = useCallback((shown: BackgroundEffect) => {
        setRawHold((hold) => (hold && backgroundEffectsEqual(hold.effect, shown) ? null : hold))
    }, [])

    // A known problem ends the hold: that pick is covered and explained, and a
    // later Retry starts from the cover, not from the raw view.
    useEffect(() => {
        if (previewProblemHolds) setRawHold(null)
    }, [previewProblemHolds])

    /** The pick the preview just showed is now the choice: save it if it was waiting. */
    const commitPreviewPick = useCallback((effect: BackgroundEffect) => {
        const pending = pendingSaveRef.current
        if (pending && backgroundEffectsEqual(pending, effect)) {
            setPendingSave(null)
            void saveBackgroundEffectPreference(effect)
        }
    }, [setPendingSave])

    useEffect(() => {
        const track = state.stream?.getVideoTracks()[0] ?? null
        const effect = backgroundEffect
        const isLatest = () => backgroundEffectsEqual(backgroundEffectRef.current, effect)

        // The processor is bound to one track, so a new stream needs a new one.
        if (previewHandleRef.current && previewHandleRef.current.track !== track) {
            releasePreviewEffect()
        }

        // No camera means no processor: it is bound to a track that no longer
        // exists, and holding its WebGL context would be pure waste.
        if (!track || !state.videoEnabled) {
            releasePreviewEffect()
            return
        }

        // Effects cannot run here. Raw only if the user chose it for this
        // device; otherwise the preview stays covered and says why.
        if (effect.kind !== 'none' && !isBackgroundEffectAvailable()) {
            releasePreviewEffect()
            setPreviewProblem(
                cameraWithoutBackground ? null : { cause: 'unsupported', effect }
            )
            return
        }

        const reusable = previewHandleRef.current
        if (effect.kind === 'none') {
            setPreviewProblem(null)
            // An attach for the previous choice may still be in flight; nothing
            // else would stop it landing an effect the user just turned off.
            previewEffectAbortRef.current?.abort()
            previewEffectAbortRef.current = null
            if (!reusable) return
            // Switched to pass-through rather than destroyed: swapping the
            // displayed stream back to the raw camera would reset the element
            // to a black frame before it decodes.
            const pending = {}
            previewSwitchPendingRef.current = pending
            previewSwitchesInFlightRef.current.add(pending)
            void reusable.handle.switchTo(NO_BACKGROUND_EFFECT).then((result) => {
                previewSwitchesInFlightRef.current.delete(pending)
                if (previewHandleRef.current !== reusable || result.superseded) return
                if (result.ok) previewProcessorShowsRef.current = NO_BACKGROUND_EFFECT
                if (previewSwitchPendingRef.current === pending) previewSwitchPendingRef.current = null
                // Painted even if the user has picked again since: pass-through
                // is what is on screen, and "none" keeps the preview covered
                // until the newer pick's switch lands.
                if (result.ok) showInPreview(NO_BACKGROUND_EFFECT)
                else releasePreviewEffect()
            })
            return
        }

        // Already on screen — typically the effect a failed switch fell back
        // to. Nothing to do, and the notice about that failure stays up. Not
        // while another switch is under way, though: a pick back to what is on
        // screen must supersede that switch, or it lands the abandoned pick.
        const alreadyChosenOnScreen =
            reusable &&
            !previewSwitchPendingRef.current &&
            backgroundEffectsEqual(previewShowingRef.current, effect) &&
            previewRetryTokenRef.current === previewRetryToken
        if (
            alreadyChosenOnScreen &&
            previewSwitchesInFlightRef.current.size === 0 &&
            backgroundEffectsEqual(previewProcessorShowsRef.current, effect)
        ) {
            return
        }
        previewRetryTokenRef.current = previewRetryToken
        // R8-N1: the processor is (or may yet be) on a pick the user left, so
        // it is switched back — but this is the same restore, and the notice
        // about the failure that caused it stays up.
        if (!alreadyChosenOnScreen) setPreviewProblem(null)

        if (reusable) {
            // Switch in place. The previous effect, if any, stays on screen
            // until the new one is in.
            const fallback = previewShowingRef.current
            // Back to the effect on screen — the R8-N1 restore, or a pick back
            // to it. Its image was shown moments ago, so the cache will do: no
            // deletion check, and no wait on a network that may just have
            // failed the pick being undone (R9-N1).
            const backToOnScreen = backgroundEffectsEqual(fallback, effect)
            const pending = {}
            previewSwitchPendingRef.current = pending
            previewSwitchesInFlightRef.current.add(pending)
            setPreviewSwitching('download')
            const switched = backToOnScreen
                ? reusable.handle.switchTo(effect, { revalidate: false })
                : reusable.handle.switchTo(effect)
            void switched.then((result) => {
                previewSwitchesInFlightRef.current.delete(pending)
                if (previewHandleRef.current !== reusable || result.superseded) return
                // Recorded whether or not the user still wants it: it is
                // what the processor renders until another switch lands.
                if (result.ok) previewProcessorShowsRef.current = effect
                setPreviewSwitching(null)
                // A pick the user has since left. A switch the processor
                // had already begun cannot stand down, so it can report
                // success here; the newer pick's switch, queued behind it
                // or issued by the next run, decides what is shown. Painted
                // now, it would read as the background on screen.
                if (!isLatest()) return
                if (previewSwitchPendingRef.current === pending) previewSwitchPendingRef.current = null
                if (result.ok) {
                    showInPreview(effect)
                    endRawHold(effect)
                    commitPreviewPick(effect)
                    return
                }
                // R9-N1: the R8-N1 restore itself failed, the processor
                // untouched. The notice about the pick that caused it stays,
                // so Retry still retries that pick; replaced by one about the
                // effect on screen, Retry would re-pick the selection and do
                // nothing.
                if (alreadyChosenOnScreen && result.restored) {
                    // What is on screen is the pick: it is the choice now, and
                    // nothing else will ever commit it, so Join must not wait
                    // for it. The notice stays.
                    commitPreviewPick(effect)
                    return
                }
                if (fallback.kind !== 'none' && result.restored) {
                    // The pick that failed is not kept: the effect still on
                    // screen stays the choice, and the call is told so.
                    setPendingSave(null)
                    setBackgroundEffect(fallback)
                    requestBackgroundEffect(fallback, false)
                    setPreviewProblem({ cause: 'switch-failed', effect, stillUsing: fallback })
                    return
                }
                // Nothing chosen is on screen any more: covered, and why.
                if (!result.restored) releasePreviewEffect()
                else showInPreview(NO_BACKGROUND_EFFECT)
                setPreviewProblem({ cause: result.failure ?? 'processor-failed', effect })
            })
            return
        }

        // Abort any attach still in flight before starting another, so a
        // superseded one cannot hold its WebGL context for the life of the page.
        previewEffectAbortRef.current?.abort()
        const controller = new AbortController()
        previewEffectAbortRef.current = controller

        // Read now, not after an await: the raise effect above has just run in
        // this same commit, so a raise this pick set off is already recorded.
        const pendingRaise = captureRaiseRef.current
        const raise = pendingRaise?.track === track ? pendingRaise.settled : null

        void (async () => {
            // The processor starts only once the resolution it will be fed is in
            // place, so it never has to resize right as the preview appears (see
            // the raise effect): the attach waits for the raise just before
            // init(), and fetches the image meanwhile. Nothing is shown meanwhile
            // that is not already: the preview stays covered, or on the raw view
            // the pick was made over.
            // Set once this attach's handle is the preview's. From then on
            // in-place switches change the effect without a new attach (and a
            // switch to "none" drops the controller), so the slow notice names
            // what the processor shows now, not the effect it started with.
            let attachedHandle: PreviewBackgroundHandle | null = null
            const result = await attachPreviewBackgroundEffect({
                sourceTrack: track,
                effect,
                signal: controller.signal,
                beforeInit: raise ? () => raise : undefined,
                onSlow: () => {
                    let showing: BackgroundEffect
                    if (attachedHandle) {
                        if (previewHandleRef.current?.handle !== attachedHandle) return
                        showing = previewProcessorShowsRef.current
                    } else {
                        if (previewEffectAbortRef.current !== controller) return
                        showing = effect
                    }
                    // Pass-through costs nothing: there is no effect to blame.
                    if (showing.kind === 'none') return
                    setPreviewProblem((current) => current ?? { cause: 'slow', effect: showing })
                },
                // The step-down to the lighter model replaced the processor and
                // its stream. Bound as a new stream, the element plays it and the
                // preview stays covered until it has painted, as for any other;
                // a track swapped inside the stream it already holds would do
                // neither, and Safari leaves such an element paused.
                onStreamReplaced: (replacement) => {
                    if (!attachedHandle) return
                    if (previewHandleRef.current?.handle !== attachedHandle) return
                    setProcessedPreviewStream(replacement)
                },
            })
            if (!result) {
                // Not abandoned, so the camera track had already ended: there is
                // no live view left to hold, and a dead one is a frozen frame.
                if (!controller.signal.aborted) endRawHold(effect)
                return
            }
            if (controller.signal.aborted || previewEffectAbortRef.current !== controller) {
                result.handle?.stop()
                return
            }
            if (!result.handle) {
                setPreviewProblem({ cause: result.failure ?? 'processor-failed', effect })
                return
            }
            const handle = result.handle
            previewHandleRef.current = { track, handle }
            previewProcessorShowsRef.current = effect
            attachedHandle = handle
            previewStopRef.current = releasePreviewEffect
            // Held over the raw view: the surfaces keep it beneath their element
            // until the processed stream has painted there. In the same render
            // as the stream itself, or the element would blank uncovered.
            const heldRaw = rawHeldStreamRef.current
            setPreviewHandover(heldRaw ? { from: heldRaw, to: handle.stream } : null)
            setProcessedPreviewStream(handle.stream)
            showInPreview(effect)
            endRawHold(effect)
            if (isLatest()) commitPreviewPick(effect)
        })()

        // Deliberately no cleanup: this effect re-runs on every effect change,
        // and tearing the processor down there is exactly the flash we are
        // avoiding. Teardown is explicit above, and `previewStopRef` still
        // covers unmount, a new stream, and the handoff to LiveKit.
    }, [
        state.stream,
        state.videoEnabled,
        backgroundEffect,
        previewRetryToken,
        cameraWithoutBackground,
        releasePreviewEffect,
        showInPreview,
        commitPreviewPick,
        endRawHold,
        setPendingSave,
    ])

    /**
     * SC-158: record the user's pre-join choice.
     *
     * Persisted immediately rather than on join, because the room connector
     * reads the very same store when it starts — that is what carries the
     * choice across the handoff, with no extra plumbing between the two.
     */
    const handleBackgroundEffectChange = useCallback((effect: BackgroundEffect) => {
        setBackgroundEffect(effect)
        // Made over the raw view, uncovered: that view stays until this pick is
        // in. Set in the same batch as the choice, so no render shows the
        // choice without the hold. A pick made over anything else (an effect,
        // the cover) gets none.
        const rawTrack = rawViewOnScreenRef.current
        setRawHold(effect.kind !== 'none' && rawTrack ? { effect, track: rawTrack } : null)
        // Saved now unless the preview is showing another effect it could keep
        // instead: then only once this one is on screen (see the preview
        // effect). A pick with nothing to fall back on is the choice at once.
        const previewHasEffect =
            previewHandleRef.current !== null && previewShowingRef.current.kind !== 'none'
        // A pick of what the processor already shows, with no switch under way,
        // runs no switch and so would never be "shown" to commit it: it is
        // the choice at once, or Join would wait for it for ever.
        const alreadyShown =
            !previewSwitchPendingRef.current &&
            previewSwitchesInFlightRef.current.size === 0 &&
            backgroundEffectsEqual(previewShowingRef.current, effect) &&
            backgroundEffectsEqual(previewProcessorShowsRef.current, effect)
        if (effect.kind !== 'none' && previewHasEffect && !alreadyShown) {
            setPendingSave(effect)
        } else {
            setPendingSave(null)
            void saveBackgroundEffectPreference(effect)
        }
        // Tell the room connector too, for the case where it is already mounted:
        // it seeds its own effect once, at mount, so without this a choice made
        // here would not reach an existing session until the next reload.
        // `persist: false` — the save above already covers it.
        requestBackgroundEffect(effect, false)
    }, [setPendingSave])

    /** Try the selected effect again: the Join screen's Retry. */
    const retryBackgroundEffect = useCallback(() => {
        // A failed switch is retried as the pick that failed, not the effect
        // still on screen — that one is fine. As a fresh pick, so the fallback
        // stays the choice, and is what is saved, until the retry shows.
        if (previewProblem?.cause === 'switch-failed') {
            setPreviewProblem(null)
            handleBackgroundEffectChange(previewProblem.effect)
            return
        }
        setPreviewProblem(null)
        setPreviewRetryToken((token) => token + 1)
        // A call already running retries too.
        requestBackgroundEffect(backgroundEffectRef.current, false)
    }, [previewProblem, handleBackgroundEffectChange])

    /**
     * "Turn on camera without background": this device only, never the saved
     * cross-device choice, which still applies wherever it can run.
     */
    const enableCameraWithoutBackground = useCallback(() => {
        writeCameraWithoutBackgroundOnThisDevice(true)
        setCameraWithoutBackground(true)
    }, [])

    /**
     * Requests permissions and enumerates devices, then sets initial state.
     */
    const requestPermissionsAndEnumerateDevices = useCallback(async () => {
        dispatch({ type: 'SET_PERMISSIONS_REQUESTED', requested: true })
        dispatch({ type: 'SET_FORCED_OFF', kind: 'video', forced: false })
        dispatch({ type: 'SET_FORCED_OFF', kind: 'audio', forced: false })
        // Honour the last user preference stored in localStorage. Missing key =
        // on (first time); an off that an error forced is tried again. Read once,
        // before anything can turn a device off, so only a device the user wanted
        // counts as forced off below.
        const wantsVideo = readMediaPreferenceForJoin('video')
        const wantsAudio = readMediaPreferenceForJoin('audio')
        // Read by the catch, to name the device in the busy message.
        let microphoneOnlyRequested = false
        try {
            // Request access to media devices.
            // Use the saved preferred device IDs (ideal — not exact, so the browser can
            // fall back gracefully if the device is unplugged or the ID is stale) so the
            // initial stream opens on the correct camera immediately. Without this, the
            // browser picks whichever camera it enumerates first (often the built-in), even
            // when the user has an external camera set as their preferred device.
            const savedVideoDeviceId = localStorage.getItem('preferredVideoDevice') || ''
            const savedAudioDeviceId = localStorage.getItem('preferredAudioDevice') || ''
            const initVideoEnabled = wantsVideo
            const initAudioEnabled = wantsAudio
            // getUserMedia requires at least one of audio/video to be truthy.
            // If both are saved as false (edge case), request both for permission grant
            // and rely on getMediaStream below to apply the correct constraints.
            const mustRequestBoth = !initVideoEnabled && !initAudioEnabled
            microphoneOnlyRequested = !mustRequestBoth && !initVideoEnabled
            // SC-377: that request is only for the right to list the devices, and
            // it blinks the camera light for a preview that shows nothing. Not
            // needed where both are granted and the list comes back with ids
            // (Safari hides them until something is captured).
            let alreadyListed: MediaDeviceInfo[] | null = null
            if (mustRequestBoth && (await bothDevicesAlreadyGranted())) {
                const listed = await navigator.mediaDevices.enumerateDevices()
                if (listed.every((device) => Boolean(device.deviceId))) alreadyListed = listed
            }
            const {
                stream: initStream,
                videoError,
                audioError,
            } = alreadyListed !== null
                ? { stream: null as MediaStream | null, videoError: null, audioError: null }
                : await acquireInitialStream(
                      (mustRequestBoth || initVideoEnabled) ? (savedVideoDeviceId ? { deviceId: { ideal: savedVideoDeviceId } } : true) : false,
                      (mustRequestBoth || initAudioEnabled) ? (savedAudioDeviceId ? { deviceId: { ideal: savedAudioDeviceId } } : true) : false
                  )
            currentStreamRef.current = initStream
            dispatch({ type: 'SET_STREAM', stream: initStream })
            // At least one device opened, or both were granted already, so the
            // device pickers stay usable.
            dispatch({ type: 'SET_PERMISSIONS_GRANTED', granted: true })

            // Get saved device preferences
            const savedVideoDevice = localStorage.getItem('preferredVideoDevice') || ''
            const savedAudioDevice = localStorage.getItem('preferredAudioDevice') || ''
            let savedVideoEnabled = wantsVideo
            let savedAudioEnabled = wantsAudio
            // A device that would not open is off for this session. Join saves the
            // dialog's state, so only a device that really failed is saved off,
            // and marked forced so the next Join screen tries it again.
            if (videoError) savedVideoEnabled = false
            if (audioError) savedAudioEnabled = false
            if (videoError && wantsVideo) dispatch({ type: 'SET_FORCED_OFF', kind: 'video', forced: true })
            if (audioError && wantsAudio) dispatch({ type: 'SET_FORCED_OFF', kind: 'audio', forced: true })

            dispatch({ type: 'SET_VIDEO_ENABLED', enabled: savedVideoEnabled })
            dispatch({ type: 'SET_AUDIO_ENABLED', enabled: savedAudioEnabled })

            // Enumerate devices. A list taken above stands: nothing was captured
            // since, so asking again could not name anything new. Otherwise a
            // capture just happened, and only now are the ids filled in.
            const devices = alreadyListed ?? (await navigator.mediaDevices.enumerateDevices())
            const videoDevices = devices.filter((device) => device.kind === 'videoinput')
            const audioDevices = devices.filter((device) => device.kind === 'audioinput')

            console.log('[useMediaDevices] Enumerated devices:', {
                videoCount: videoDevices.length,
                audioCount: audioDevices.length,
                videoEnabled: savedVideoEnabled,
                audioEnabled: savedAudioEnabled
            })

            // Check if we have devices for enabled media types. Shown once the
            // stream call below is done: it clears both device errors as it starts.
            let missingCameraMessage: string | null = null
            let missingMicrophoneMessage: string | null = null
            if (savedVideoEnabled && videoDevices.length === 0) {
                console.warn('[useMediaDevices] Video enabled but no video devices found')
                missingCameraMessage = 'No camera devices found. Please connect a camera or disable video.'
                // Disable video if no devices available
                savedVideoEnabled = false
                dispatch({ type: 'SET_VIDEO_ENABLED', enabled: false })
                dispatch({ type: 'SET_FORCED_OFF', kind: 'video', forced: true })
            }

            if (savedAudioEnabled && audioDevices.length === 0) {
                console.warn('[useMediaDevices] Audio enabled but no audio devices found')
                missingMicrophoneMessage = 'No microphone devices found. Please connect a microphone or disable audio.'
                // Disable audio if no devices available
                savedAudioEnabled = false
                dispatch({ type: 'SET_AUDIO_ENABLED', enabled: false })
                dispatch({ type: 'SET_FORCED_OFF', kind: 'audio', forced: true })
            }

            const showDeviceProblems = () => {
                if (missingCameraMessage) {
                    dispatch({ type: 'SET_CAMERA_ERROR', cameraError: missingCameraMessage })
                }
                if (missingMicrophoneMessage) {
                    dispatch({
                        type: 'SET_MICROPHONE_ERROR',
                        microphoneError: missingMicrophoneMessage,
                    })
                }
                // A device the user had turned off was only opened because both
                // were off and getUserMedia needs one: its failure is not theirs.
                if (videoError && wantsVideo) {
                    dispatch({
                        type: 'SET_CAMERA_ERROR',
                        cameraError: describeDeviceFailure('camera', videoError),
                    })
                }
                if (audioError && wantsAudio) {
                    dispatch({
                        type: 'SET_MICROPHONE_ERROR',
                        microphoneError: describeDeviceFailure('microphone', audioError),
                    })
                }
            }

            // Both off is a choice the user can make, so it is not overridden here:
            // turning a device back on would publish what they had turned off.
            if (
                !savedVideoEnabled &&
                !savedAudioEnabled &&
                videoDevices.length === 0 &&
                audioDevices.length === 0
            ) {
                dispatch({
                    type: 'SET_ERROR',
                    error: 'No camera or microphone found. You can still join without them.',
                })
                showDeviceProblems()
                dispatch({ type: 'SET_INITIALIZING', initializing: false })
                dispatch({ type: 'SET_VIDEO_ENABLED', enabled: false })
                dispatch({ type: 'SET_AUDIO_ENABLED', enabled: false })
                return
            }

            // Validate saved device IDs
            const selectedVideoDevice = pickListedDevice(savedVideoDevice, videoDevices)
            const selectedAudioDevice = pickListedDevice(savedAudioDevice, audioDevices)

            listedDevicesRef.current = { video: videoDevices, audio: audioDevices }
            dispatch({ type: 'SET_DEVICES', videoDevices, audioDevices })
            dispatch({ type: 'SET_SELECTED_VIDEO_DEVICE', deviceId: selectedVideoDevice })
            dispatch({ type: 'SET_SELECTED_AUDIO_DEVICE', deviceId: selectedAudioDevice })
            dispatch({ type: 'SET_INITIALIZING', initializing: false })

            // Apply validated device selections with corrected enabled states
            await getMediaStream(
                selectedVideoDevice,
                selectedAudioDevice,
                savedVideoEnabled,
                savedAudioEnabled
            )
            showDeviceProblems()
        } catch (error) {
            let errorMsg = 'Failed to access camera and microphone. Please check your permissions.'
            if (isBusyDeviceError(error)) {
                errorMsg = microphoneOnlyRequested ? MICROPHONE_BUSY_MESSAGE : CAMERA_BUSY_MESSAGE
            }
            // Report the problem, but do NOT treat it as a reason to keep the
            // user out. No camera, a broken camera, a camera held by another app,
            // or a denied prompt all end here — and none of them should block
            // entry to the office. They join without media and can fix it later.
            dispatch({ type: 'SET_ERROR', error: `${errorMsg} You can still join without them.` })
            dispatch({ type: 'SET_INITIALIZING', initializing: false })
            dispatch({ type: 'SET_PERMISSIONS_GRANTED', granted: false })
            dispatch({ type: 'SET_VIDEO_ENABLED', enabled: false })
            dispatch({ type: 'SET_AUDIO_ENABLED', enabled: false })
            // Join saves both off, since LiveKit must not publish them; marked
            // forced so a later grant is not met by devices still off. A device
            // the user had turned off stays a choice.
            if (wantsVideo) dispatch({ type: 'SET_FORCED_OFF', kind: 'video', forced: true })
            if (wantsAudio) dispatch({ type: 'SET_FORCED_OFF', kind: 'audio', forced: true })
        }
    }, [])

    /**
     * Lists the devices again after a kind has opened, when the list the selects
     * hold for it could not name its devices. Nothing else lists again: a camera
     * turned on in the dialog is granted only now, and without this the select
     * keeps an id with no option while Join checks its choice against a list that
     * cannot name the device the preview opened.
     *
     * Best effort: the device is already open, so a failure only leaves the list
     * as it was. Once per call, never from an effect, so a browser that keeps its
     * ids back cannot loop it.
     */
    const relistOpenedDevices = useCallback(
        async (
            opened: { video: boolean; audio: boolean },
            selected: { video: string; audio: string }
        ) => {
            const before = listedDevicesRef.current
            const video = opened.video && listedWithoutIds(before.video)
            const audio = opened.audio && listedWithoutIds(before.audio)
            if (!video && !audio) return

            try {
                const devices = await navigator.mediaDevices.enumerateDevices()
                // Read again: a refresh may have listed during the await.
                const current = listedDevicesRef.current
                const ofKind = (kind: MediaDeviceKind) =>
                    devices.filter((device) => device.kind === kind)
                const next = {
                    video: video ? ofKind('videoinput') : current.video,
                    audio: audio ? ofKind('audioinput') : current.audio,
                }
                listedDevicesRef.current = next
                dispatch({
                    type: 'SET_DEVICES',
                    videoDevices: next.video,
                    audioDevices: next.audio,
                })
                if (video) {
                    const pick = pickListedDevice(selected.video, next.video)
                    if (pick !== selected.video) {
                        dispatch({ type: 'SET_SELECTED_VIDEO_DEVICE', deviceId: pick })
                    }
                }
                if (audio) {
                    const pick = pickListedDevice(selected.audio, next.audio)
                    if (pick !== selected.audio) {
                        dispatch({ type: 'SET_SELECTED_AUDIO_DEVICE', deviceId: pick })
                    }
                }
            } catch (error) {
                console.warn('[useMediaDevices] Could not list the devices again:', error)
            }
        },
        []
    )

    /**
     * Gets a media stream with the specified devices and updates state.
     * Bug B fix: Uses plain getUserMedia instead of the noise-suppressor pipeline.
     * The noise suppressor is expensive, can fail/hang on some devices, and is only
     * needed for actual LiveKit track publishing — not for the preview dialog.
     * Also includes a timeout guard to ensure loading:true never gets stuck.
     */
    const getMediaStream = useCallback(async (videoDeviceId: string, audioDeviceId: string, forceVideoEnabled?: boolean, forceAudioEnabled?: boolean) => {
        dispatch({ type: 'SET_LOADING', loading: true })
        dispatch({ type: 'SET_CAMERA_ERROR', cameraError: null })
        dispatch({ type: 'SET_MICROPHONE_ERROR', microphoneError: null })
        dispatch({ type: 'SET_ERROR', error: null })

        // Bug B: Timeout guard — if getMediaStream takes longer than 10s, force loading off
        const loadingTimeoutId = setTimeout(() => {
            console.warn('[useMediaDevices] getMediaStream timeout — forcing loading:false')
            dispatch({ type: 'SET_LOADING', loading: false })
        }, 10000)

        try {
            // Release previous stream (no noise-suppressor pipeline to clean up anymore)
            previewEffectAbortRef.current?.abort()
            previewEffectAbortRef.current = null
            if (previewStopRef.current) {
                previewStopRef.current()
                previewStopRef.current = null
            }
            if (currentStreamRef.current) {
                currentStreamRef.current.getTracks().forEach((track) => track.stop())
                currentStreamRef.current = null
            }

            // Use forced values if provided, otherwise use state values
            const videoEnabled = forceVideoEnabled !== undefined ? forceVideoEnabled : state.videoEnabled
            const audioEnabled = forceAudioEnabled !== undefined ? forceAudioEnabled : state.audioEnabled

            // CRITICAL: Browser requires at least one of audio or video to be requested
            // If both are disabled, we cannot request a stream, so clear the stream and return
            if (!videoEnabled && !audioEnabled) {
                console.log('[useMediaDevices] Both video and audio disabled, clearing stream')
                dispatch({ type: 'SET_STREAM', stream: null })
                dispatch({ type: 'SET_LOADING', loading: false })
                clearTimeout(loadingTimeoutId)
                return null
            }

            // Bug B fix: Build constraints using plain getUserMedia for the preview.
            // No noise-suppressor pipeline needed here — it's only used for LiveKit publishing.
            // SC-158: an effect needs the detail. See BACKGROUND_EFFECT_VIDEO_PRESET —
            // segmentation quality is bounded by capture resolution, and the library's
            // fixed 8px mask feather erases finger gaps at 360p.
            // An effect that will not run here (flag off, unsupported device)
            // gets the ordinary resolution.
            const effectActive = isBackgroundEffectActive(backgroundEffectRef.current)
            const baseVideo = getGetUserMediaVideoConstraints(
                undefined,
                videoPresetForBackgroundEffect(undefined, effectActive)
            )
            const constraints: MediaStreamConstraints = {
                video: videoEnabled ? {
                    ...baseVideo,
                    deviceId: videoDeviceId ? { exact: videoDeviceId } : undefined,
                } : false,
                audio: audioEnabled ? getDefaultAudioConstraints(audioDeviceId, { exactDeviceId: true }) : false,
            }

            let newStream: MediaStream
            try {
                newStream = await navigator.mediaDevices.getUserMedia(constraints)
            } catch (exactError) {
                // Fallback: try with ideal constraints instead of exact device IDs
                console.warn('[useMediaDevices] Exact device match failed, trying ideal constraints:', exactError)
                const fallbackConstraints: MediaStreamConstraints = {
                    video: videoEnabled
                        ? getGetUserMediaVideoConstraints(
                              videoDeviceId,
                              videoPresetForBackgroundEffect(undefined, effectActive)
                          )
                        : false,
                    audio: audioEnabled
                        ? getDefaultAudioConstraints(audioDeviceId, {
                            exactDeviceId: false,
                            preferCompatibility: true,
                        })
                        : false,
                }
                newStream = await navigator.mediaDevices.getUserMedia(fallbackConstraints)
            }

            currentStreamRef.current = newStream
            dispatch({ type: 'SET_STREAM', stream: newStream })
            dispatch({ type: 'SET_LOADING', loading: false })
            dispatch({ type: 'SET_ERROR', error: null })
            clearTimeout(loadingTimeoutId)
            await relistOpenedDevices(
                { video: videoEnabled, audio: audioEnabled },
                { video: videoDeviceId, audio: audioDeviceId }
            )
            return newStream
        } catch (error) {
            console.error('[useMediaDevices] Error getting media stream:', error)
            clearTimeout(loadingTimeoutId)
            const errorObj = error as Error & { name?: string; constraint?: string }
            const errorMessage = errorObj.message || 'Unknown error'
            const errorName = errorObj.name || ''

            // Provide user-friendly error messages
            let userFriendlyMessage = ''
            if (errorName === 'NotAllowedError' || errorMessage.includes('permission')) {
                userFriendlyMessage = 'Camera or microphone access was denied. Please allow access in your browser settings and try again.'
            } else if (errorName === 'NotFoundError' || errorMessage.includes('not found')) {
                userFriendlyMessage = 'Camera or microphone not found. Please check that your device is connected and try again.'
            } else if (errorName === 'NotReadableError' || errorMessage.includes('not readable')) {
                userFriendlyMessage = 'Camera or microphone is being used by another application. Please close other apps and try again.'
            } else if (errorName === 'OverconstrainedError' || errorMessage.includes('constraint')) {
                userFriendlyMessage = 'The selected device does not support the required settings. Please try a different device.'
            } else if (errorMessage.includes('At least one of audio and video must be requested')) {
                userFriendlyMessage = 'Please enable at least camera or microphone to continue.'
            } else {
                userFriendlyMessage = `Unable to access your camera or microphone: ${errorMessage}`
            }

            // Determine which device failed based on error context
            if (errorMessage.toLowerCase().includes('video') || errorMessage.toLowerCase().includes('camera') ||
                (errorObj.constraint && errorObj.constraint.includes('video'))) {
                dispatch({ type: 'SET_CAMERA_ERROR', cameraError: userFriendlyMessage })
            } else if (errorMessage.toLowerCase().includes('audio') || errorMessage.toLowerCase().includes('microphone') ||
                (errorObj.constraint && errorObj.constraint.includes('audio'))) {
                dispatch({ type: 'SET_MICROPHONE_ERROR', microphoneError: userFriendlyMessage })
            } else {
                dispatch({ type: 'SET_ERROR', error: userFriendlyMessage })
            }

            dispatch({ type: 'SET_LOADING', loading: false })
            return null
        }
    }, [state.videoEnabled, state.audioEnabled, relistOpenedDevices])

    /**
     * Handles device selection (video/audio)
     * Clears errors and updates stream with new device
     */
    const handleDeviceSelection = useCallback(async (type: 'video' | 'audio', deviceId: string) => {
        if (!deviceId) {
            console.warn(`[useMediaDevices] Attempted to select empty ${type} device`)
            return
        }

        console.log(`[useMediaDevices] Selecting ${type} device:`, deviceId)

        if (type === 'video') {
            dispatch({ type: 'SET_SELECTED_VIDEO_DEVICE', deviceId })
            dispatch({ type: 'SET_CAMERA_ERROR', cameraError: null })
            dispatch({ type: 'SET_ERROR', error: null })
            localStorage.setItem('preferredVideoDevice', deviceId)
        } else {
            dispatch({ type: 'SET_SELECTED_AUDIO_DEVICE', deviceId })
            dispatch({ type: 'SET_MICROPHONE_ERROR', microphoneError: null })
            dispatch({ type: 'SET_ERROR', error: null })
            localStorage.setItem('preferredAudioDevice', deviceId)
        }

        // Only update stream if the selected device type is enabled
        const shouldUpdateVideo = type === 'video' && state.videoEnabled
        const shouldUpdateAudio = type === 'audio' && state.audioEnabled

        // If we're selecting a device for a disabled media type, just update the selection
        // The stream will be updated when that media type is enabled
        if ((type === 'video' && !state.videoEnabled) || (type === 'audio' && !state.audioEnabled)) {
            console.log(`[useMediaDevices] ${type} is disabled, only updating device selection`)
            return
        }

        await getMediaStream(
            type === 'video' ? deviceId : state.selectedVideoDevice,
            type === 'audio' ? deviceId : state.selectedAudioDevice
        )
    }, [getMediaStream, state.selectedVideoDevice, state.selectedAudioDevice, state.videoEnabled, state.audioEnabled])

    /**
     * Toggles video on/off. Both devices may be off at once.
     * Uses track.enabled toggling on the existing stream instead of stopping
     * all tracks and calling getUserMedia again. This avoids a race condition
     * where the browser hasn't fully released the camera after track.stop(),
     * causing the re-acquire to fail with NotReadableError/AbortError.
     */
    const handleVideoToggle = useCallback(async () => {
        const newVideoEnabled = !state.videoEnabled
        // A failed turn-on. The user asked for it on, so the off is forced and
        // the next Join screen tries the camera again.
        const revertVideoOn = () => {
            dispatch({ type: 'SET_VIDEO_ENABLED', enabled: false })
            dispatch({ type: 'SET_FORCED_OFF', kind: 'video', forced: true })
            writeMediaPreference('video', false, { forced: true })
        }

        // Both off is allowed. Entering the office is not conditional on
        // publishing media — a user may have no working devices at all, and a
        // user who simply wants to look around should not be forced to broadcast.
        // (getUserMedia itself still needs at least one track requested; that is
        // handled where the stream is acquired, not here.)
        dispatch({ type: 'SET_VIDEO_ENABLED', enabled: newVideoEnabled })
        dispatch({ type: 'SET_CAMERA_ERROR', cameraError: null })
        // The user's own choice now, whatever forced it before.
        dispatch({ type: 'SET_FORCED_OFF', kind: 'video', forced: false })
        writeMediaPreference('video', newVideoEnabled)

        const stream = currentStreamRef.current
        if (stream) {
            const videoTracks = stream.getVideoTracks()
            if (newVideoEnabled && videoTracks.length === 0) {
                // No video track exists on the current stream (e.g. was created
                // audio-only during initialization). Fall back to full rebuild.
                const result = await getMediaStream(state.selectedVideoDevice, state.selectedAudioDevice, true, state.audioEnabled)
                if (result === null) {
                    // getMediaStream failed — revert optimistic state so the user
                    // cannot join with a broken camera state.
                    revertVideoOn()
                }
            } else {
                // Toggle the enabled property on existing video tracks.
                // This keeps the camera lock and avoids the stop/re-acquire cycle.
                videoTracks.forEach(t => { t.enabled = newVideoEnabled })
            }
        } else if (newVideoEnabled) {
            // No stream at all — create one from scratch
            const result = await getMediaStream(state.selectedVideoDevice, state.selectedAudioDevice, true, state.audioEnabled)
            if (result === null) revertVideoOn()
        }
    }, [state.videoEnabled, state.audioEnabled, state.selectedVideoDevice, state.selectedAudioDevice, getMediaStream])

    /**
     * Toggles audio on/off. Both devices may be off at once.
     * Uses track.enabled toggling on the existing stream (same rationale as
     * handleVideoToggle — avoids destructive stop/re-acquire cycle).
     */
    const handleAudioToggle = useCallback(async () => {
        const newAudioEnabled = !state.audioEnabled
        // A failed turn-on: forced off, as for the camera.
        const revertAudioOn = () => {
            dispatch({ type: 'SET_AUDIO_ENABLED', enabled: false })
            dispatch({ type: 'SET_FORCED_OFF', kind: 'audio', forced: true })
            writeMediaPreference('audio', false, { forced: true })
        }

        // Both off is allowed — see handleVideoToggle.
        dispatch({ type: 'SET_AUDIO_ENABLED', enabled: newAudioEnabled })
        dispatch({ type: 'SET_MICROPHONE_ERROR', microphoneError: null })
        dispatch({ type: 'SET_FORCED_OFF', kind: 'audio', forced: false })
        writeMediaPreference('audio', newAudioEnabled)

        const stream = currentStreamRef.current
        if (stream) {
            const audioTracks = stream.getAudioTracks()
            if (newAudioEnabled && audioTracks.length === 0) {
                // No audio track on current stream — need a full rebuild
                const result = await getMediaStream(state.selectedVideoDevice, state.selectedAudioDevice, state.videoEnabled, true)
                if (result === null) {
                    // getMediaStream failed — revert optimistic state
                    revertAudioOn()
                }
            } else {
                // Toggle the enabled property on existing audio tracks
                audioTracks.forEach(t => { t.enabled = newAudioEnabled })
            }
        } else if (newAudioEnabled) {
            // No stream at all — create one from scratch
            const result = await getMediaStream(state.selectedVideoDevice, state.selectedAudioDevice, state.videoEnabled, true)
            if (result === null) revertAudioOn()
        }
    }, [state.videoEnabled, state.audioEnabled, state.selectedVideoDevice, state.selectedAudioDevice, getMediaStream])

    /**
     * Bug C fix: Synchronously stops all preview stream tracks and releases the camera/mic.
     * Must be called before handing off to LiveKit so the device is not held exclusively.
     */
    const stopPreviewTracks = useCallback(() => {
        // Joining with a pick the preview had not finished showing: the call
        // applies it from here, so it is the choice the user is joining with.
        const pending = pendingSaveRef.current
        setPendingSave(null)
        if (pending) void saveBackgroundEffectPreference(pending)
        previewEffectAbortRef.current?.abort()
        previewEffectAbortRef.current = null
        if (previewStopRef.current) {
            previewStopRef.current()
            previewStopRef.current = null
        }
        if (currentStreamRef.current) {
            currentStreamRef.current.getTracks().forEach((track) => track.stop())
            currentStreamRef.current = null
        }
        dispatch({ type: 'SET_STREAM', stream: null })
    }, [setPendingSave])

    /**
     * Refreshes device list and resets preferences
     * Re-initializes the entire media device setup
     */
    const handleRefresh = useCallback(async () => {
        console.log('[useMediaDevices] Refreshing devices...')
        dispatch({ type: 'SET_LOADING', loading: true })
        dispatch({ type: 'SET_ERROR', error: null })
        dispatch({ type: 'SET_CAMERA_ERROR', cameraError: null })
        dispatch({ type: 'SET_MICROPHONE_ERROR', microphoneError: null })

        // Clean up existing stream
        if (currentStreamRef.current) {
            currentStreamRef.current.getTracks().forEach((track) => track.stop())
            currentStreamRef.current = null
            dispatch({ type: 'SET_STREAM', stream: null })
        }

        // Honour saved preferences on refresh (same logic as init).
        const refreshVideoEnabled = readMediaPreferenceForJoin('video')
        const refreshAudioEnabled = readMediaPreferenceForJoin('audio')
        dispatch({ type: 'SET_VIDEO_ENABLED', enabled: refreshVideoEnabled })
        dispatch({ type: 'SET_AUDIO_ENABLED', enabled: refreshAudioEnabled })

        // Re-request permissions and enumerate devices
        try {
            await requestPermissionsAndEnumerateDevices()
        } catch (error) {
            console.error('[useMediaDevices] Error refreshing devices:', error)
            dispatch({ type: 'SET_ERROR', error: 'Failed to refresh devices. Please try again.' })
        } finally {
            // The only place loading ends for a refresh that fails: the call above swallows
            // its own errors, so whatever way it ended, Join must not stay disabled.
            dispatch({ type: 'SET_LOADING', loading: false })
        }
    }, [requestPermissionsAndEnumerateDevices])

    return {
        state,
        setState: dispatch,
        videoRef,
        handleDeviceSelection,
        handleVideoToggle,
        handleAudioToggle,
        handleRefresh,
        stopPreviewTracks,
        backgroundEffect,
        handleBackgroundEffectChange,
        previewShowing,
        previewStream,
        previewHandoverFrom,
        previewUnderlayStream,
        previewEffectPending,
        previewEffectWorking,
        previewPainted,
        previewProblem,
        backgroundEffectApplying,
        retryBackgroundEffect,
        enableCameraWithoutBackground,
    }
} 
